import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../supabaseClient'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const CURRENT_MONTH = 4
const FIN_KEYS = ['gross_revenue', 'net_profit', 'mrr_under_contract', 'mrr_clients', 'active_clients']
const MKT_KEYS = ['raw_leads', 'qualified_leads', 'fta', 'new_customers_closed', 'new_customer_mrr', 'total_contract_value']
const MONEY_KEYS = new Set(['gross_revenue', 'net_profit', 'mrr_under_contract', 'new_customer_mrr', 'total_contract_value'])

function fmtVal(v, key) {
  if (v === null || v === undefined) return '—'
  const isMoney = MONEY_KEYS.has(key)
  const abs = Math.abs(v)
  let s
  if (isMoney) {
    if (abs >= 1000000) s = '$' + (abs / 1000000).toFixed(1) + 'M'
    else if (abs >= 1000) s = '$' + Math.round(abs / 1000) + 'K'
    else s = '$' + abs.toLocaleString()
  } else {
    s = abs >= 1000 ? (abs / 1000).toFixed(1) + 'K' : abs.toString()
  }
  return v < 0 ? '-' + s : s
}

function parseGoalInput(val) {
  const clean = val.replace(/[$,]/g, '').trim()
  let num = parseFloat(clean)
  if (clean.toUpperCase().endsWith('M')) num = parseFloat(clean) * 1000000
  else if (clean.toUpperCase().endsWith('K')) num = parseFloat(clean) * 1000
  return isNaN(num) ? null : Math.round(num)
}

export default function KPIDashboard() {
  const [goals, setGoals] = useState({})
  const [actuals, setActuals] = useState({})
  const [goalEdits, setGoalEdits] = useState({})
  const [loading, setLoading] = useState(true)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const [{ data: goalsData }, { data: actualsData }] = await Promise.all([
        supabase.from('kpi_goals').select('*').eq('year', 2026),
        supabase.from('kpi_actuals').select('*').eq('year', 2026),
      ])
      const goalsMap = {}
      ;(goalsData || []).forEach(g => { goalsMap[g.kpi_key] = g })
      setGoals(goalsMap)
      const actualsMap = {}
      ;(actualsData || []).forEach(a => {
        if (!actualsMap[a.kpi_key]) actualsMap[a.kpi_key] = {}
        actualsMap[a.kpi_key][a.month] = a.actual_value
      })
      setActuals(actualsMap)
    } catch (err) {
      console.error('Failed to load KPI data', err)
    }
    setLoading(false)
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const updateGoal = async (key, rawVal) => {
    const num = parseGoalInput(rawVal)
    if (num === null) return
    setGoals(prev => ({ ...prev, [key]: { ...prev[key], goal_value: num } }))
    await supabase.from('kpi_goals').update({ goal_value: num }).eq('year', 2026).eq('kpi_key', key)
  }

  const ytd = (key) => {
    if (!actuals[key]) return 0
    return Object.values(actuals[key]).reduce((s, v) => s + v, 0)
  }

  const pctOfYear = CURRENT_MONTH / 12

  function KPIRow({ kpiKey }) {
    const goal = goals[kpiKey]
    if (!goal) return null
    const goalVal = goal.goal_value
    const ytdVal = ytd(kpiKey)
    const pct = goalVal === 0 ? null : Math.round((ytdVal / goalVal) * 100)
    const onPace = goalVal === 0 ? null : ytdVal >= goalVal * pctOfYear * 0.85
    const editVal = goalEdits[kpiKey]

    let pillEl, fillColor
    if (goalVal === 0) { pillEl = <span className="pill pill-warn">Watch</span>; fillColor = '#f5a623' }
    else if (pct >= 100) { pillEl = <span className="pill pill-green">{pct}%</span>; fillColor = '#34c97b' }
    else if (pct >= 25) { pillEl = <span className="pill pill-warn">{pct}%</span>; fillColor = '#f5a623' }
    else { pillEl = <span className="pill pill-red">{pct}%</span>; fillColor = '#f25c5c' }

    const paceEl = onPace === null
      ? <span className="pill pill-warn">Monitor</span>
      : onPace ? <span className="pill pill-green">On pace</span> : <span className="pill pill-red">Behind</span>

    const fillW = goalVal === 0 ? 30 : Math.min(100, pct || 0)

    return (
      <tr>
        <td>{goal.kpi_label}{kpiKey === 'net_profit' && <span style={{ fontSize: 10, color: 'var(--red)', marginLeft: 6 }}>▲ watch</span>}</td>
        <td>
          <input
            className="goal-input"
            value={editVal !== undefined ? editVal : fmtVal(goalVal, kpiKey)}
            onChange={e => setGoalEdits(prev => ({ ...prev, [kpiKey]: e.target.value }))}
            onBlur={e => { updateGoal(kpiKey, e.target.value); setGoalEdits(prev => { const n = { ...prev }; delete n[kpiKey]; return n }) }}
            onFocus={e => e.target.select()}
          />
        </td>
        <td style={{ fontWeight: 500 }}>{fmtVal(ytdVal, kpiKey)}</td>
        <td>{pillEl}</td>
        {MONTHS.slice(0, CURRENT_MONTH).map((_, i) => (
          <td key={i} className={i === CURRENT_MONTH - 1 ? 'strong' : ''} style={{ fontSize: 12 }}>
            {actuals[kpiKey]?.[i + 1] !== undefined ? fmtVal(actuals[kpiKey][i + 1], kpiKey) : '—'}
          </td>
        ))}
        {MONTHS.slice(CURRENT_MONTH).map(m => (
          <td key={m} style={{ fontSize: 12, color: 'var(--text-dim)' }}>—</td>
        ))}
        <td><div className="progress-wrap"><div className="progress-bar"><div className="progress-fill" style={{ width: `${fillW}%`, background: fillColor }} /></div></div></td>
        <td>{paceEl}</td>
      </tr>
    )
  }

  const ebitdaYTD = ytd('net_profit')
  if (loading) return <div className="loading">Loading KPI data...</div>

  return (
    <div>
      <div className="page-header">
        <div className="page-title">KPI Dashboard 2026</div>
        <div className="page-sub">Goal vs. actual · Click any goal to edit it</div>
      </div>
      {ebitdaYTD < 0 && (
        <div className="alert-banner">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5"/><rect x="7.25" y="4" width="1.5" height="5" rx=".75" fill="currentColor"/><rect x="7.25" y="10.5" width="1.5" height="1.5" rx=".75" fill="currentColor"/></svg>
          EBITDA is {fmtVal(ebitdaYTD, 'net_profit')} YTD. April turned positive at $87,800 — monitor closely.
        </div>
      )}
      <div style={{ marginBottom: 28 }}>
        <div className="kpi-section-title">Company financial KPIs</div>
        <div className="card">
          <div className="table-wrap">
            <table className="kpi-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: 180 }}>KPI</th>
                  <th style={{ width: 100 }}>Goal 2026</th>
                  <th style={{ width: 90 }}>YTD actual</th>
                  <th style={{ width: 70 }}>vs goal</th>
                  {MONTHS.slice(0, CURRENT_MONTH).map(m => <th key={m} style={{ width: 65 }}>{m}</th>)}
                  {MONTHS.slice(CURRENT_MONTH).map(m => <th key={m} style={{ width: 55, color: 'var(--text-dim)' }}>{m}</th>)}
                  <th style={{ width: 80 }}>Progress</th>
                  <th style={{ width: 80 }}>Pace</th>
                </tr>
              </thead>
              <tbody>{FIN_KEYS.map(k => <KPIRow key={k} kpiKey={k} />)}</tbody>
            </table>
          </div>
        </div>
      </div>
      <div>
        <div className="kpi-section-title">Sales and marketing funnel</div>
        <div className="card">
          <div className="table-wrap">
            <table className="kpi-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', width: 180 }}>KPI</th>
                  <th style={{ width: 100 }}>Goal 2026</th>
                  <th style={{ width: 90 }}>YTD actual</th>
                  <th style={{ width: 70 }}>vs goal</th>
                  {MONTHS.slice(0, CURRENT_MONTH).map(m => <th key={m} style={{ width: 65 }}>{m}</th>)}
                  {MONTHS.slice(CURRENT_MONTH).map(m => <th key={m} style={{ width: 55, color: 'var(--text-dim)' }}>{m}</th>)}
                  <th style={{ width: 80 }}>Progress</th>
                  <th style={{ width: 80 }}>Pace</th>
                </tr>
              </thead>
              <tbody>{MKT_KEYS.map(k => <KPIRow key={k} kpiKey={k} />)}</tbody>
            </table>
          </div>
          <div className="table-footer"><span>Financial data from QuickBooks · Funnel data from Keap / KQM · Goals editable</span></div>
        </div>
      </div>
    </div>
  )
}
